import React, { createContext, useState, useEffect, useContext } from "react";
import { ConfigContext } from "../security/ConfigContext";
import useSecurity from "../hooks/useSecurity";
import UserUtils from "../utils/UserUtils";
import ConfigUtils from "../utils/ConfigUtils";
import _ from "lodash";
import LoadingSpinner from "../components/common/LoadingSpinner";
import useStoreConfig from "../hooks/useStoreConfig";
import ErrorRedirect from "../components/error-redirect/ErrorRedirect";
import useAxios from "../adapter/useAxios";
import { UserRoles } from "../constants/UserRoles";
import { SocketContext } from "./SocketContext"; 
import { Sockets } from "../constants/Sockets";
import useWebSocket from "../hooks/useWebSocket";
import { useLocation, useHistory } from "react-router-dom";


const initialState = {
  isUserLoading: false,
  lists: [],
  userList: [],
  onUpdateUser: (_user, _dept) => {},
  _setFavoritesRequestsIds: (_id) => {},
  _setLoading: (_flag) => {},
  department: [],
  favoriteRequestsIds: [],
  assignedTo: [],
  getFilters: (_userVal) => {},
  fetchUsers: () => {},
  display: true,
  filterList: "",
  userInfo: {},
  userRole: "",
  getAdminOps: () => {},
  getDepartments: () => {},
  _setIsLoading: (_flag) => {},
  _setError: () => {},
  initialFetch: true,
  faspexAccountMapping: []
};

export const UserContext = createContext(initialState);
UserContext.displayName = "UserListContext";

export const UserContextProvider = ({ children }) => {
  const { pathname } = useLocation();
  const [config] = useContext(ConfigContext);
  const [isUserLoading, setLoading] = useState(initialState.isUserLoading);
  const { ConfigStore } = useStoreConfig();
  const { userInfo } = useSecurity();
  const [lists, setList] = useState(initialState.lists);
  const [department, setDepartment] = useState(initialState.department);
  const [favoriteRequestsIds, setFavoritesRequestsIds] = useState(initialState.favoriteRequestsIds);
  const [assignedTo, setAssignee] = useState(initialState.assignedTo);
  const [display, setDisplay] = useState(initialState.display);
  const [userList, setUserList] = useState(initialState.userList);
  const [filterList, setFilterList] = useState(ConfigStore.filtersField());
  const [userRole, setRole] = useState(initialState.userRole);
  const [userVal, setUserVal] = useState({});
  const [initialLoading, setIsLoading] = useState(true);
  const [error, setError] = useState({});
  const [initialFetch, setInitialFetch] = useState(initialState.initialFetch);
  const [faspexAccountMapping, setFaspexAccountMapping] = useState(initialState.faspexAccountMapping);
  const {put, get} = useAxios();
  const {socket, _setWebsocketConnection} = useContext(SocketContext)
  const { connectSocket } = useWebSocket();
  const history = useHistory();
  const isUserManagementPage = pathname === "/users";
  const errorRedirectPaths = pathname === "/users" || pathname === "/lists";

  const _setLoading = (flag) => {
    setLoading(flag);
    setDisplay(false);
  };
  const handleResponse = (response) => {
    const filteredList = _.filter(response, function (o) {
      return !_.isEmpty(o.SAMAccountName);
    });
    const uniqList = _.uniq(filteredList);
    setUserList(UserUtils.transformGrp(uniqList));
    setList(UserUtils.transformGrp(uniqList));
    setDepartment(UserUtils.getDepartments(uniqList));
    setAssignee(UserUtils.getAssignee(uniqList));
  };
  const _setIsLoading = (flag) => {
    setIsLoading(flag);
  };
  const getAdminOps = () => {
    return assignedTo;
  };
  const getDepartments = () => {
    return department;
  };

  const fetchData = async() => {
    try{
      const response = await get({ url: 'initialize' });
      // Set UI Configs
      ConfigUtils.setConfig(response.jobDefinitions);
      const loggedInUser = response.user;
      if (loggedInUser && loggedInUser.deptName && loggedInUser.deptName.length > 0) {
        setFavoritesRequestsIds(loggedInUser.favoriteRequestsIds || []);
        setRole(UserUtils.getRoleFromConfig(_.get(userInfo, "ops.morph.groups")));
      } else {
        const errorObj = { message: "User is not assigned to a Department.", errorCode: "access_denied" };
        setError(errorObj);
        return ;
      }
    }catch {
      const errorObj = { message: "User is not assigned to a Department.", errorCode: "access_denied" };
      setError(errorObj);
    }finally {
      setIsLoading(false);
    }
  }

  const fetchUsers = () => {
    setLoading(true);
    const urlParam = isUserManagementPage ? "users?noCaching=true" : "users";
    get({ url: urlParam })
      .then((response) => {
        setDisplay(true);
        handleResponse(response);
      })
      .catch(() => {
        const errorObj = { message: "User is not assigned to a Department.", errorCode: "access_denied" };
        // Setting role to null to render error view
        setRole(null);
        setIsLoading(false);
        setError(errorObj);
      })
      .finally(() => {
        setInitialFetch(false);
        setLoading(false);
      });
  };

  const fetchFaspexAccountConfig = async () => {
    try {
      const response = await get({ url: 'content-request/faspexAccountMapping' });
      setFaspexAccountMapping(response || []);
    } catch (error) {
      console.error('Failed to fetch faspex account mapping:', error);
      setFaspexAccountMapping([]);
    }
  };

  const _setError = (role?) => {
    let errorObj = {
      type: "restricted",
      message: "User is not authorised to access this page.",
      errorCode: "access_denied",
      redirect: role === UserRoles.CR_API ? true : false
    };
    setError(errorObj);
  };
  
  useEffect(() => {
    if (!_.isEmpty(userInfo)) {
      let assignedGoups = Object.values(_.get(userInfo, "ops.morph.groups")).filter((group) => {
        return _.includes(group, "GlobalAdmin") || _.includes(group, "SuperAdmin") || _.includes(group, "AdminWithReadAccess") || _.includes(group, "Ops") ||  _.includes(group, "Requestor") || _.includes(group, "Admin") || _.includes(group, "MediaRequest") || _.includes(group, "CR_API") || _.includes(group, "Legacy_GMSC");
      });
      if (assignedGoups.length > 1 ) {
        setIsLoading(false);
        let errorObj = { message: "User is assigned to more than one role.", errorCode: "access_denied" };
        setError(errorObj);
        return;
      }
      const role = UserUtils.getRoleFromConfig(_.get(userInfo, "ops.morph.groups"));
      if ((pathname === "/" || pathname === "/summary" || pathname === "/requestSummary") && role === UserRoles.LEGACY_GMSC) {
        history.push("/legacyGmsc");
      } else if (errorRedirectPaths && role !== UserRoles.ADMIN && role !== UserRoles.SUPERADMIN && role !== UserRoles.GLOBALADMIN) {
        if(role === UserRoles.CR_API) {
          _setError(role);
        } else {
          setIsLoading(false);
          _setError();
          _setLoading(false);
          return;
        }
      }
      // Check if user belongs to one of the departments or exists in our colleciton.
      fetchData();
      fetchUsers();
      fetchFaspexAccountConfig();
    }
    setUserVal(userInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo]);

  const getFilters = (userValue) => {
    return _.map(filterList, (filter) => {
      if (filter.label === "Department") {
        if (userRole === "requestor") {
          filter.values = UserUtils.getFilterDept(_.get(userValue, "userDepartments"));
        } else {
          filter.values = UserUtils.getFilterDept(department);
        }
      }
      if (filter.label === "Assigned To") {
        filter.values = UserUtils.getFilterAssignee(assignedTo);
      }
      return filter;
    });
  };

  useEffect(() => {
    if (!_.isEmpty(userRole)) {
      setIsLoading(false);
    }
    setFilterList(getFilters(UserUtils.getUser(userInfo, lists)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedTo, department, userRole]);

  useEffect(() => {
    setFilterList(ConfigStore.filtersField());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ConfigUtils.getCustomFilters()]);
  
  const webSocketConnection = async () => {
    const ws = await connectSocket();
    _setWebsocketConnection(ws);
  };

  useEffect(() => {
    if (!_.isEmpty(userRole)) {
      if (!socket) webSocketConnection();
      if (socket) return () => socket.close(Sockets.closingTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, userRole]);

  const _setFavoritesRequestsIds = (id) => {
    setFavoritesRequestsIds(id);
  };

  const onUpdateUser = (user, dept) => {
    if (_.isEmpty(user)) {
      return;
    }    
    setLoading(true);
    put({
      url: config.apiUrls.users + user,
      data: dept
    })
    .then((response) => {
      console.log(response);
      fetchUsers();
    })
    .catch((errorMsg) => {
      console.log(errorMsg);
    })
  };
  
  return (
    <UserContext.Provider
      value={{
        isUserLoading,
        lists,
        onUpdateUser,
        _setFavoritesRequestsIds,
        _setLoading,
        department,
        favoriteRequestsIds,
        assignedTo,
        getFilters,
        fetchUsers,
        display,
        userList,
        filterList,
        userInfo: userVal,
        userRole,
        getAdminOps,
        getDepartments,
        _setIsLoading,
        _setError,
        initialFetch,
        faspexAccountMapping
      }}>
      {_.isEmpty(error) || error['redirect'] ? children : null}
      <LoadingSpinner show={isUserManagementPage ? isUserLoading || initialLoading : initialLoading} />
      <ErrorRedirect error={error} />
    </UserContext.Provider>
  );
};